import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Autocomplete,
  TextField
} from '@mui/material';
import { useModalContext } from './ModalContext';
import { greenhouseEventTypeInfo } from './greenhouseEventTypeInfo';
import { greenhouseOnboardingEventTypeInfo } from './greenhouseOnboardingEventTypeInfo';
import {
  SEED_TALENT_UI_TYPES,
  fixedEventTypeFor
} from './seedTalentIntegrationTypes';

const UI_TYPES = [
  { value: '', label: 'Other / Custom (raw JSON)' },
  { value: 'greenhouse-recruiting', label: 'Greenhouse Recruiting' },
  { value: 'greenhouse-onboarding', label: 'Greenhouse Onboarding' },
  ...SEED_TALENT_UI_TYPES
];

// Maps a UI type to the client/eventType list its EventTypeDropdown should offer.
const EVENT_TYPE_INFO_BY_UI_TYPE = {
  'greenhouse-recruiting': greenhouseEventTypeInfo,
  'greenhouse-onboarding': greenhouseOnboardingEventTypeInfo
};

const UiTypeDropdown = ({ disabled, value, onChange }) => {
  const option = UI_TYPES.find((o) => o.value === value) || UI_TYPES[0];
  return (
    <Autocomplete
      disableClearable
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(e, newValue) => onChange(newValue.value)}
      options={UI_TYPES}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label="Integration type"
          margin="dense"
          variant="outlined"
        />
      )}
      value={option}
    />
  );
};

const EventTypeDropdown = ({ disabled, value, onChange, eventTypeInfo }) => {
  const options = Object.keys(eventTypeInfo).map((eventType) => ({
    value: eventType,
    label: eventTypeInfo[eventType].label
  }));
  const option = options.find((o) => o.value === value) || null;
  return (
    <Autocomplete
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(e, newValue) => onChange(newValue ? newValue.value : '')}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label="Client"
          margin="dense"
          required
          variant="outlined"
        />
      )}
      value={option}
    />
  );
};

export const IntegrationTypeCard = () => {
  const { uiType, setUiType, formState, setFormState, isUpdate } =
    useModalContext();

  const onUiTypeChange = (newUiType) => {
    setUiType(newUiType);
    const seedTalentEventType = fixedEventTypeFor(newUiType);
    if (seedTalentEventType) {
      setFormState({
        ...formState,
        values: { ...formState.values, eventType: seedTalentEventType }
      });
      return;
    }
    const eventTypeInfo = EVENT_TYPE_INFO_BY_UI_TYPE[newUiType];
    if (eventTypeInfo && !eventTypeInfo[formState.values.eventType]) {
      setFormState({
        ...formState,
        values: { ...formState.values, eventType: '' }
      });
    }
  };

  const onEventTypeChange = (newEventType) => {
    setFormState({
      ...formState,
      values: { ...formState.values, eventType: newEventType }
    });
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title="Integration type"
        titleTypographyProps={{ sx: { fontWeight: 700 } }}
      />
      <Divider />
      <CardContent>
        <UiTypeDropdown
          disabled={isUpdate}
          onChange={onUiTypeChange}
          value={uiType}
        />
        {EVENT_TYPE_INFO_BY_UI_TYPE[uiType] && (
          <EventTypeDropdown
            disabled={isUpdate}
            eventTypeInfo={EVENT_TYPE_INFO_BY_UI_TYPE[uiType]}
            onChange={onEventTypeChange}
            value={formState.values.eventType}
          />
        )}
        {fixedEventTypeFor(uiType) && (
          <TextField
            disabled
            fullWidth
            label="Event type"
            margin="dense"
            value={fixedEventTypeFor(uiType)}
            variant="outlined"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrationTypeCard;
